import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { LanguageSwitcher } from '@/components/features/language/LanguageSwitcher';
import { TimezoneWidget } from '@/components/features/timezone/TimezoneWidget';
import { UserNav } from '@/components/features/user/UserNav';
import { FactorySelector } from '@/components/features/factory/FactorySelector';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { AppFooter } from '@/components/layout/AppFooter';
import { NotificationBell } from '@/components/features/alerts/NotificationBell';
import { SidebarNavContent } from './sidebar-nav-content';
import { MobileNav } from './mobile-nav';

const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';

export function DashboardLayout(): React.JSX.Element {
  // Sidebar collapse state (persisted to localStorage) — desktop rail only.
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return stored === 'true';
  });

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed));
  }, [isCollapsed]);

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar — full-height column, desktop rail only; MobileNav's drawer covers viewports below md */}
      <aside
        className={cn(
          'sticky top-0 hidden h-screen shrink-0 border-r bg-card shadow-md transition-all duration-300 md:block',
          isCollapsed ? 'w-16' : 'w-64'
        )}
      >
        <SidebarNavContent
          isCollapsed={isCollapsed}
          showCollapseToggle
          onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
        />
      </aside>

      {/* Content column — header runs alongside the sidebar, not above it */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Navbar - White Label Ready */}
        <header className="sticky top-0 z-50 border-b bg-card shadow-sm backdrop-blur-sm">
          <div className="flex h-16 items-center justify-between gap-2 px-3 md:px-6">
            {/* Left: mobile nav trigger + factory context */}
            <div className="flex items-center gap-2 md:gap-3">
              <MobileNav />
              <FactorySelector />
            </div>

            {/* Center: clock — anchored to the sticky <header> (nearest positioned
                ancestor), so it's truly centered on the full header bar regardless
                of how wide the left/right content is. A grid-based center column
                would give the empty mobile-hidden slot equal width to the right
                section, pushing UserNav to the middle instead of the right edge —
                absolute positioning avoids that entirely. Hidden below md, see
                MobileNav drawer. */}
            <div className="hidden md:absolute md:left-1/2 md:top-1/2 md:block md:-translate-x-1/2 md:-translate-y-1/2">
              <TimezoneWidget />
            </div>

            {/* Right: user controls (language/theme hidden below md — see MobileNav drawer) */}
            <div className="flex items-center gap-2 md:gap-3">
              <NotificationBell />
              <div className="hidden items-center gap-3 md:flex">
                <div className="h-6 w-px bg-border" />
                <LanguageSwitcher />
                <div className="h-6 w-px bg-border" />
                <ThemeToggle />
              </div>
              <UserNav />
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 space-y-6 p-4 md:p-6 md:pb-20">
          <Outlet />
        </main>
      </div>

      <AppFooter isCollapsed={isCollapsed} />
    </div>
  );
}
